import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getChats, readAllChatMessages } from "../../api/api";
import { useInboxSubscription, useActiveInboxChat, useUserRealtime } from "../../context/UserRealtimeContext";
import { useMessagingSearch } from "../../context/MessagingSearchContext";
import { ChatList } from "./ChatList";
import ChatFrame from "./chat_frame/ChatFrame";
import NewChatModal from "./NewChatModal";
import InboxSearchResults from "./InboxSearchResults";
import SignedOutPrompt from "../SignedOutPrompt";
import { isAccountVerified } from "../utils";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import {
  bumpChatForNewMessage,
  clearChatUnread,
  getSharedMessagePreview,
  shouldIncrementUnread,
  sortChatsByRecent,
  upsertChat,
  mergeChatByRecent,
} from "./chatListUtils";
import { useInboxSearch } from "./useInboxSearch";

/**
 * MessagingPage — full-height under the glass header; lists scroll internally.
 * On mobile: conversation list OR chat frame (with back), never both.
 * On desktop: two-column list + chat.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
function MessagingPage({ userId, user }) {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const { query: searchQuery, showResults, dismissResults } = useMessagingSearch();
  const inboxSearch = useInboxSearch({
    userId,
    query: searchQuery,
    enabled: showResults,
  });
  const [focusMessageId, setFocusMessageId] = useState(() => {
    const id = location.state?.focusMessageId;
    return id != null ? Number(id) : null;
  });
  const [focusKey, setFocusKey] = useState(0);

  const hasChat = Boolean(chatId);
  const routeChatId = chatId ? parseInt(chatId, 10) : null;
  const openChatIdRef = useRef(routeChatId);
  openChatIdRef.current = routeChatId;
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const { reportUnreadCleared } = useUserRealtime();
  useActiveInboxChat(routeChatId, null);

  const seededChat =
    location.state?.chat &&
    routeChatId != null &&
    Number(location.state.chat.id) === routeChatId
      ? location.state.chat
      : null;

  const refreshChats = useCallback(async () => {
    if (!userId) {
      setChats([]);
      return;
    }
    try {
      const res = await getChats(userId);
      if (res.success) {
        const fromApi = sortChatsByRecent(
          Array.isArray(res.data) ? res.data : []
        );
        setChats((prev) => {
          const keep =
            seededChat &&
            !fromApi.some((c) => Number(c.id) === Number(seededChat.id))
              ? seededChat
              : prev.find(
                  (c) =>
                    routeChatId != null &&
                    Number(c.id) === routeChatId &&
                    !fromApi.some((a) => Number(a.id) === routeChatId)
                );
          return keep ? mergeChatByRecent(fromApi, keep) : fromApi;
        });
      }
    } catch {
      // Keep existing list on transient failures.
    }
  }, [userId, seededChat, routeChatId]);

  useEffect(() => {
    if (!userId) {
      setChats([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      await refreshChats();
      setLoading(false);
    })();
    // Refetch when the signed-in user changes; seed is merged if present at fetch time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Seed selection immediately from navigation state (profile Message button).
  useEffect(() => {
    if (!seededChat) return;
    setSelectedChat((prev) =>
      prev?.id === seededChat.id ? { ...prev, ...seededChat } : seededChat
    );
    setChats((prev) => mergeChatByRecent(prev, seededChat));
  }, [seededChat]);

  useEffect(() => {
    if (!chatId || !userId || loading) {
      if (!chatId) setSelectedChat(null);
      return;
    }

    const id = parseInt(chatId, 10);
    const chat = chats.find((c) => Number(c.id) === id);
    if (chat) {
      // Keep selection in sync with list metadata without remounting the frame.
      setSelectedChat((prev) =>
        prev?.id === chat.id ? { ...prev, ...chat } : chat
      );
      return;
    }

    if (seededChat) {
      setSelectedChat((prev) =>
        prev?.id === seededChat.id ? prev : seededChat
      );
      return;
    }

    navigate("/messaging");
  }, [chatId, chats, loading, userId, navigate, seededChat]);

  const updateChatLastMessage = useCallback((id, messageContent, messageTimestamp) => {
    const chatIdNum = parseInt(id, 10);
    setChats((prevChats) =>
      bumpChatForNewMessage(prevChats, chatIdNum, messageContent, messageTimestamp)
    );
    setSelectedChat((prevSelected) => {
      if (prevSelected && prevSelected.id === chatIdNum) {
        const ts =
          messageTimestamp ||
          prevSelected.last_message_timestamp ||
          prevSelected.last_message_at ||
          "";
        return {
          ...prevSelected,
          last_message_content: messageContent,
          last_message_timestamp: ts,
          last_message_at: ts || prevSelected.last_message_at,
        };
      }
      return prevSelected;
    });
  }, []);

  const handleInboxMessage = useCallback(
    (message) => {
      const chatIdNum = Number(message?.chat_id);
      if (!Number.isFinite(chatIdNum)) return;

      const content = getSharedMessagePreview(message);
      const ts = message.created_at || "";
      const increment = shouldIncrementUnread(
        message,
        userId,
        openChatIdRef.current
      );

      let missingChat = false;
      setChats((prev) => {
        const exists = (prev || []).some((c) => Number(c.id) === chatIdNum);
        if (!exists) {
          missingChat = true;
          return prev;
        }
        return bumpChatForNewMessage(prev, chatIdNum, content, ts, {
          incrementUnread: increment,
        });
      });
      if (missingChat) {
        // New membership / unknown chat — heal from API.
        refreshChats();
      }

      setSelectedChat((prevSelected) => {
        if (prevSelected && Number(prevSelected.id) === chatIdNum) {
          return {
            ...prevSelected,
            last_message_content: content,
            last_message_timestamp: ts || prevSelected.last_message_timestamp,
            last_message_at: ts || prevSelected.last_message_at,
          };
        }
        return prevSelected;
      });
    },
    [userId, refreshChats]
  );

  const handleInboxChatRemoved = useCallback(
    (removedChatId) => {
      setChats((prev) =>
        (prev || []).filter((c) => Number(c.id) !== Number(removedChatId))
      );
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(removedChatId) ? null : prev
      );
      if (routeChatId != null && Number(routeChatId) === Number(removedChatId)) {
        navigate("/messaging");
      }
    },
    [navigate, routeChatId]
  );

  useInboxSubscription({
    onInboxMessage: handleInboxMessage,
    onChatRemoved: handleInboxChatRemoved,
    onReconnect: refreshChats,
  });

  const handleChatCreated = (newChat) => {
    setChats((prev) => upsertChat(prev, newChat));
    setSelectedChat(newChat);
    navigate(`/messaging/${newChat.id}`, { state: { chat: newChat } });
  };

  /**
   * Keep list + selection in sync when chat metadata changes (e.g. members added).
   * @param {object} updated
   * @returns {void}
   */
  const handleChatUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    setChats((prev) => mergeChatByRecent(prev, updated));
    setSelectedChat((prev) =>
      prev && Number(prev.id) === Number(updated.id) ? { ...prev, ...updated } : prev
    );
  }, []);

  /**
   * Leave the open chat when the current user is removed remotely.
   * @param {number} removedChatId
   * @returns {void}
   */
  const handleRemovedFromChat = useCallback(
    (removedChatId) => {
      if (!removedChatId) return;
      setChats((prev) =>
        prev.filter((c) => Number(c.id) !== Number(removedChatId))
      );
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(removedChatId) ? null : prev
      );
      navigate("/messaging");
    },
    [navigate]
  );

  const handleMarkChatRead = useCallback(
    (id) => {
      const chat = (chatsRef.current || []).find(
        (c) => Number(c.id) === Number(id)
      );
      const cleared = Number(chat?.unread_count) || 0;
      if (cleared > 0) {
        reportUnreadCleared(cleared, null);
      }
      setChats((prev) => clearChatUnread(prev, id));
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(id)
          ? { ...prev, unread_count: 0 }
          : prev
      );
    },
    [reportUnreadCleared]
  );

  // Deep-link / route open: clear unread for the active chat whenever it is non-zero.
  useEffect(() => {
    if (!userId || routeChatId == null) return;
    const unread = chats.find((c) => Number(c.id) === routeChatId)?.unread_count;
    if (!(Number(unread) > 0)) return;
    handleMarkChatRead(routeChatId);
    readAllChatMessages(routeChatId, userId).catch(() => {});
  }, [userId, routeChatId, chats, handleMarkChatRead]);

  const handleBackToList = useCallback(() => {
    navigate("/messaging");
  }, [navigate]);

  const openChatFromSearch = useCallback(
    (chat, messageId = null) => {
      if (!chat?.id) return;
      if (messageId != null) {
        setFocusMessageId(Number(messageId));
        setFocusKey((k) => k + 1);
        navigate(`/messaging/${chat.id}`, {
          state: { focusMessageId: Number(messageId) },
        });
      } else {
        setFocusMessageId(null);
        navigate(`/messaging/${chat.id}`);
      }
      dismissResults();
    },
    [dismissResults, navigate]
  );

  const handleSearchChat = useCallback(
    (chat) => {
      openChatFromSearch(chat);
    },
    [openChatFromSearch]
  );

  const handleSearchMessage = useCallback(
    (hit) => {
      const existing = chats.find((c) => Number(c.id) === Number(hit.chat_id));
      const chat = existing || {
        id: hit.chat_id,
        name: hit.chat_name,
        chat_type: hit.chat_type,
      };
      openChatFromSearch(chat, hit.id);
    },
    [chats, openChatFromSearch]
  );

  const mobilePanelActions = useMemo(
    () => ({
      onBack: handleBackToList,
      backClassName: "md:hidden",
    }),
    [handleBackToList]
  );

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Messages"
        description="Sign in to message people and organizations in your network."
      />
    );
  }

  if (user && !isAccountVerified(user)) {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Messages"
        description="Your account must be verified before you can send messages. Until then, you can browse Explore and manage your profile."
      />
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-ui-background">
      <aside
        className={cn(
          "flex w-full flex-col border-ui-border bg-secondary/88 backdrop-blur-md md:w-80 md:shrink-0 md:border-r",
          hasChat || showResults ? "hidden md:flex" : "flex"
        )}
      >
        <div className="space-y-3 border-b border-ui-border p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-text-primary">Messages</h1>
              <p className="mt-0.5 text-sm text-text-muted">Your conversations</p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => setShowNewChatModal(true)}
              title="New chat"
            >
              New
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <ChatList
            userId={userId}
            chats={chats}
            loading={loading}
            selectedChat={selectedChat}
            onChatSelect={(chat) => {
              navigate(`/messaging/${chat.id}`);
              setFocusMessageId(null);
              dismissResults();
            }}
            onChatRead={handleMarkChatRead}
            emptyLabel="No chats found."
          />
        </div>
      </aside>

      <div
        className={cn(
          "min-h-0 min-w-0 flex-1 flex-col",
          hasChat || showResults ? "flex" : "hidden md:flex"
        )}
      >
        {showResults ? (
          <InboxSearchResults
            userId={userId}
            query={searchQuery}
            chats={inboxSearch.chats}
            messages={inboxSearch.messages}
            loading={inboxSearch.loading}
            error={inboxSearch.error}
            onSelectChat={handleSearchChat}
            onSelectMessage={handleSearchMessage}
            onChatRead={handleMarkChatRead}
            onBack={dismissResults}
          />
        ) : (
          <ChatFrame
            key={chatId || selectedChat?.id || "empty"}
            userId={userId}
            user={user}
            chat={selectedChat}
            chatId={chatId ? parseInt(chatId, 10) : selectedChat?.id}
            onMessageUpdate={updateChatLastMessage}
            panelActions={mobilePanelActions}
            currentUserPfp={user?.pfp_url}
            onChatUpdate={handleChatUpdate}
            onRemovedFromChat={handleRemovedFromChat}
            focusMessageId={focusMessageId}
            focusKey={focusKey}
          />
        )}
      </div>

      {showNewChatModal ? (
        <NewChatModal
          userId={userId}
          onClose={() => setShowNewChatModal(false)}
          onChatCreated={handleChatCreated}
        />
      ) : null}
    </div>
  );
}

export default MessagingPage;
